import { useEffect, useMemo, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import Header from 'components/Layouts/components/Header';
import Sidebar from './components/Sidebar';
import { useLocation, useNavigate } from 'react-router-dom';
import PaymentCardWarningModal, { PAYMENT_CARD_REMINDER_COOKIE } from '../Common/PaymentCardWarningModal';
import { useGetBusinessProfile } from '@/apis/business/hook';
import { useGetCardDetails } from '@/apis/business/setting/billing/hook';
import { getCookie } from '@/utils/helperFunctions';
import { useGetCurrentUser } from '@/apis/user/hook';
import { Navigate } from 'react-router-dom';

type Props = {
  children: JSX.Element;
};

export default function AppLayout({ children }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const isBillingPage = location.pathname.includes('billing');
  const [showPaymentWarning, setShowPaymentWarning] = useState(false);

  const { data: businessProfile } = useGetBusinessProfile();
  const { data: user, isLoading: loadingUser } = useGetCurrentUser();
  const { data: cardDetails, isLoading: isLoadingCards } = useGetCardDetails({
    enabled: Boolean(businessProfile?.agency_billing_enabled)
  });

  const memoizedAccountStatus = useMemo(() => businessProfile?.account_status, [businessProfile?.account_status]);
  // we need to restrict business status behavior for agency user
  const accountStatus =
    loadingUser || ['agency_admin', 'agency_admin_readonly', 'agent_builder'].includes(user?.role ?? '')
      ? 'active'
      : memoizedAccountStatus;
  const isPastDue = accountStatus === 'past_due';
  const isInactiveOrArchived = accountStatus === 'inactive' || accountStatus === 'archived';

  useEffect(() => {
    if (isInactiveOrArchived && location.pathname !== '/access-denied') {
      navigate('/access-denied', { replace: true });
    }
  }, [isInactiveOrArchived, location.pathname, navigate]);

  useEffect(() => {
    if (loadingUser || ['agency_admin', 'agency_admin_readonly', 'agent_builder'].includes(user?.role!)) return;
    const isReminderDismissed = getCookie(PAYMENT_CARD_REMINDER_COOKIE);
    const hasNoCards = !isLoadingCards && (!cardDetails?.data || cardDetails?.data?.length === 0);
    const isBillingEnabled = Boolean(businessProfile?.agency_billing_enabled);
    const hasBillingPlan = Boolean(businessProfile?.business_plan_id && !businessProfile?.hide_billing);

    if (isBillingEnabled && hasBillingPlan && hasNoCards && !isReminderDismissed && !isBillingPage) {
      setShowPaymentWarning(true);
    } else {
      setShowPaymentWarning(false);
    }
  }, [cardDetails, isLoadingCards, businessProfile, isBillingPage, user?.role, loadingUser]);

  if (isPastDue) {
    return <Navigate to={'/setting/billing'} />;
  }

  return (
    <>
      <Header onMenuClick={handleDrawerToggle} />
      <Box bgcolor={theme.palette.background.default} display='flex' sx={{ height: '100vh', overflowX: 'hidden' }}>
        <Sidebar isMenuOpen={mobileOpen} handleClose={handleDrawerToggle} />
        <Box
          component='main'
          sx={{
            flexGrow: 1,
            padding: { xs: 2, lg: 3 },
            paddingTop: { xs: '72px', lg: '88px' },
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          {children}
        </Box>
      </Box>

      <PaymentCardWarningModal
        open={showPaymentWarning}
        onClose={() => setShowPaymentWarning(false)}
        billingType='business'
      />
    </>
  );
}
